import { useEffect, useRef, useState } from "react";
import { NameWord } from "../../ui/accessible-name";
import { Button } from "../../ui/button";

// Reordering with buttons, so a keyboard or a screen reader can do what a drag does. Each move is
// announced in a polite live region ("Moved Front terrace to position 2 of 5"). After a move, focus stays
// on the button that was used (or on the other one when this one is now disabled at the end of the list),
// so the keyboard user can press it again.

type MoveDirection = "up" | "down";

type MoveButtonsProps = Readonly<{
  /** The id of the page text that holds the name of the row. The button names are "Move <that text> up" and "Move <that text> down". */
  nameId: string;
  index: number;
  count: number;
  disabled?: boolean;
  onMove: (delta: -1 | 1) => void;
}>;

export function MoveButtons({ nameId, index, count, disabled = false, onMove }: MoveButtonsProps) {
  const word = (text: string) => `${nameId}-${text}`;
  return (
    <span className="inline-flex gap-1 compact:gap-2">
      <NameWord id={word("move")}>Move</NameWord>
      <NameWord id={word("up")}>up</NameWord>
      <NameWord id={word("down")}>down</NameWord>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        data-move="up"
        aria-labelledby={`${word("move")} ${nameId} ${word("up")}`}
        disabled={disabled || index === 0}
        onClick={() => onMove(-1)}
      >
        <span aria-hidden="true">↑</span>
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        data-move="down"
        aria-labelledby={`${word("move")} ${nameId} ${word("down")}`}
        disabled={disabled || index === count - 1}
        onClick={() => onMove(1)}
      >
        <span aria-hidden="true">↓</span>
      </Button>
    </span>
  );
}

/** Put `data-row-key` on each row; call `requestFocus(key, direction)` right before the list changes. */
export function useMoveFocus<E extends HTMLElement = HTMLUListElement>() {
  const container = useRef<E | null>(null);
  const request = useRef<{ key: string; direction: MoveDirection } | null>(null);

  // Runs after every render: a pending request is served once, when the moved row has its new place.
  useEffect(() => {
    const pending = request.current;
    const root = container.current;
    if (pending === null || root === null) return;
    request.current = null;
    const row = [...root.querySelectorAll<HTMLElement>("[data-row-key]")].find(
      (element) => element.dataset.rowKey === pending.key,
    );
    const other: MoveDirection = pending.direction === "up" ? "down" : "up";
    const preferred = row?.querySelector<HTMLButtonElement>(
      `button[data-move="${pending.direction}"]`,
    );
    const fallback = row?.querySelector<HTMLButtonElement>(`button[data-move="${other}"]`);
    (preferred && !preferred.disabled ? preferred : fallback)?.focus();
  });

  return {
    container,
    requestFocus: (key: string, direction: MoveDirection) => {
      request.current = { key, direction };
    },
  };
}

/** A polite live region, visually hidden. `announce` replaces its text. */
export function useAnnouncer() {
  const [message, setMessage] = useState("");
  return { message, announce: setMessage };
}

export function LiveRegion({ message }: Readonly<{ message: string }>) {
  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
}
